import React, { useState, useEffect } from 'react';
import { Search, DollarSign, CreditCard, Building2, Banknote, CheckCircle, Clock, XCircle, RefreshCw, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_METHODS = {
  cash: { name: 'Cash', icon: Banknote, color: 'text-green-600' },
  check: { name: 'Check', icon: FileText, color: 'text-blue-600' },
  credit_card: { name: 'Credit Card', icon: CreditCard, color: 'text-purple-600' },
  bank_transfer: { name: 'Bank Transfer', icon: Building2, color: 'text-indigo-600' },
  other: { name: 'Other', icon: DollarSign, color: 'text-neutral-600' }
};

const PAYMENT_STATUSES = {
  completed: { name: 'Completed', color: 'bg-green-100 text-green-700' },
  pending: { name: 'Pending', color: 'bg-amber-100 text-amber-700' },
  failed: { name: 'Failed', color: 'bg-red-100 text-red-700' }
};

export const PaymentsTab = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(amount || 0);
  };

  // Generate payment number from ID
  const getPaymentNumber = (payment, index) => {
    if (payment.id) {
      return `PAY-${payment.id.substring(0, 8).toUpperCase()}`;
    }
    return `PAY-${String(index + 1).padStart(4, '0')}`;
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.reference_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMethod = !filterMethod || payment.payment_method === filterMethod;
    
    return matchesSearch && matchesMethod;
  });

  // Calculate totals
  const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div data-testid="payments-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Payments</h2>
          <p className="text-neutral-500 text-sm">View all payments received</p>
        </div>
        <Button onClick={fetchPayments} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-500">Total Payments</p>
          <p className="text-2xl font-semibold">{filteredPayments.length}</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-500">Total Amount</p>
          <p className="text-2xl font-semibold text-green-600">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-500">This Month</p>
          <p className="text-2xl font-semibold">
            {filteredPayments.filter(p => {
              const paymentDate = new Date(p.date);
              const now = new Date();
              return paymentDate.getMonth() === now.getMonth() && 
                     paymentDate.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-500">Avg Payment</p>
          <p className="text-2xl font-semibold">
            {formatCurrency(filteredPayments.length > 0 ? totalAmount / filteredPayments.length : 0)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by customer, invoice #, or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={filterMethod}
          onChange={(e) => setFilterMethod(e.target.value)}
          className="px-3 py-2 border border-neutral-200 rounded-lg text-sm"
        >
          <option value="">All Payment Methods</option>
          {Object.entries(PAYMENT_METHODS).map(([key, method]) => (
            <option key={key} value={key}>{method.name}</option>
          ))}
        </select>
      </div>

      {/* Payments Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <DollarSign className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No payments found</p>
          <p className="text-sm text-neutral-400 mt-1">Payments will appear here when invoices are paid</p>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Payment #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Customer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Invoice #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Mode</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Amount</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => {
                const method = PAYMENT_METHODS[payment.payment_method] || PAYMENT_METHODS.other;
                const MethodIcon = method.icon;
                const status = PAYMENT_STATUSES[payment.status] || PAYMENT_STATUSES.completed;
                
                return (
                  <tr key={payment.id || index} className="border-b last:border-0 hover:bg-neutral-50">
                    <td className="px-4 py-3 text-sm">{formatDate(payment.date)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-amber-600">
                        {getPaymentNumber(payment, index)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      Invoice Payment
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-sm">{payment.customer_name || '-'}</p>
                      {payment.customer_email && (
                        <p className="text-xs text-neutral-400">{payment.customer_email}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-blue-600">
                        {payment.invoice_number || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MethodIcon className={`w-4 h-4 ${method.color}`} />
                        <span className="text-sm">{method.name}</span>
                      </div>
                      {payment.reference_number && (
                        <p className="text-xs text-neutral-400 mt-0.5">Ref: {payment.reference_number}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-green-600">
                        {formatCurrency(payment.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        <CheckCircle className="w-3 h-3" />
                        {status.name}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Summary */}
      {filteredPayments.length > 0 && (
        <div className="mt-4 flex justify-end">
          <div className="bg-neutral-50 rounded-lg px-6 py-3">
            <span className="text-sm text-neutral-500 mr-4">
              Showing {filteredPayments.length} payment{filteredPayments.length !== 1 ? 's' : ''}
            </span>
            <span className="text-sm font-medium">
              Total: <span className="text-green-600">{formatCurrency(totalAmount)}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;
